import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  BookOpen, 
  Mic, 
  HelpCircle, 
  Camera, 
  Square, 
  Play, 
  Pause,
  RotateCcw,
  Check,
  Upload,
  X,
  Heart,
  Clock,
  AlertCircle,
  Tag
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../lib/firebase';

// Section options for photo tagging
const PHOTO_SECTIONS = [
  { id: '', name: 'No specific section', icon: '📷' },
  { id: 'early', name: 'Early Life & Childhood', icon: '👶' },
  { id: 'family', name: 'Family & Home', icon: '👨‍👩‍👧‍👦' },
  { id: 'education', name: 'Education & Learning', icon: '🎓' },
  { id: 'career', name: 'Career & Work', icon: '💼' },
  { id: 'love', name: 'Love & Marriage', icon: '💕' },
  { id: 'children', name: 'Children & Parenting', icon: '👶' },
  { id: 'hobbies', name: 'Hobbies & Interests', icon: '🎨' },
  { id: 'favorites', name: 'Favorites & Traditions', icon: '⭐' },
  { id: 'faith', name: 'Faith & Beliefs', icon: '🙏' },
  { id: 'military', name: 'Military Service', icon: '🎖️' },
  { id: 'wisdom', name: 'Wisdom & Reflections', icon: '💡' }
];

export default function ContributePage() {
  const { token } = useParams();
  
  // Data state
  const [invite, setInvite] = useState(null);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [step, setStep] = useState('welcome'); // welcome, memory, photos, success
  const [contributorName, setContributorName] = useState('');
  const [relationship, setRelationship] = useState('');
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  
  // Memory text
  const [memoryText, setMemoryText] = useState('');
  const [memorySection, setMemorySection] = useState('');
  
  // Photo state
  const [photos, setPhotos] = useState([]);
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoSection, setPhotoSection] = useState('');
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Cloud Functions
  const functions = getFunctions();
  const submitContributionFn = httpsCallable(functions, 'submitContribution');
  const getUploadUrlFn = httpsCallable(functions, 'getContributionUploadUrl');

  // Load invite data
  useEffect(() => {
    loadInvite();
  }, [token]);

  const loadInvite = async () => {
    try {
      // Look up invite by document ID (token is the doc ID)
      // Firestore rules allow: get on invites collection
      const inviteDoc = await getDoc(doc(db, 'invites', token));
      
      if (!inviteDoc.exists()) {
        setError('This invite link is invalid or has expired.');
        setLoading(false);
        return;
      }
      
      const inviteData = inviteDoc.data();
      
      // Check if expired
      if (inviteData.expiresAt && inviteData.expiresAt.toDate() < new Date()) {
        setError('This invite link has expired. Please ask for a new invitation.');
        setLoading(false);
        return;
      }
      
      // Check if revoked
      if (inviteData.status === 'revoked') {
        setError('This invite link is no longer valid.');
        setLoading(false);
        return;
      }
      
      setInvite({ id: inviteDoc.id, ...inviteData });
      setContributorName(inviteData.contributorName || '');
      setRelationship(inviteData.relationship || '');
      
      // =============================================
      // SECURITY FIX: Use data from the invite document
      // instead of reading lifeStories directly.
      // Contributors are unauthenticated — Firestore rules
      // require isOwner() for lifeStories reads, so the
      // old getDoc() call would fail. The invite already
      // contains residentName which is all we need for
      // display purposes and building storage paths.
      // =============================================
      setStory({
        id: inviteData.storyId,
        residentName: inviteData.residentName || 'your loved one',
        preferredName: inviteData.residentName || 'your loved one'
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading invite:', err);
      setError('Failed to load invitation. Please try again.');
      setLoading(false);
    }
  };

  // ============================================
  // Recording functions
  // ============================================

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setDuration(0);
      
      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    } catch (err) {
      console.error('Recording error:', err);
      alert('Could not access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(timerRef.current);
  };

  const resetRecording = () => {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setDuration(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  const playAudio = () => {
    if (!audioUrl) return;
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => setIsPlaying(false);
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // ============================================
  // Photo functions
  // ============================================

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.slice(0, 5 - photos.length).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      caption: '',
      section: ''
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(photos[index].preview);
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // ============================================
  // Submit via Cloud Function
  // ============================================

  const submitContribution = async (type) => {
    if (!contributorName.trim()) {
      alert('Please enter your name');
      return;
    }
    
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      if (type === 'memory') {
        let uploadedAudioPath = null;
        
        // Upload audio via signed URL if present
        if (audioBlob) {
          const audioFileName = `contribution_${Date.now()}.webm`;
          
          // Get signed upload URL from Cloud Function
          const urlResult = await getUploadUrlFn({
            token: token,
            fileName: audioFileName,
            contentType: audioBlob.type || 'audio/webm'
          });
          
          // Upload to signed URL
          await fetch(urlResult.data.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': audioBlob.type || 'audio/webm' },
            body: audioBlob
          });
          
          uploadedAudioPath = urlResult.data.storagePath;
        }
        
        // Submit via Cloud Function (validates invite token server-side)
        await submitContributionFn({
          inviteToken: token,
          contributorName: contributorName.trim(),
          relationship: relationship.trim() || null,
          type: 'memory',
          content: memoryText.trim() || null,
          audioUrl: uploadedAudioPath,
          section: memorySection || null,
          photos: []
        });
        
      } else if (type === 'photos') {
        // Upload each photo via signed URL, then submit via Cloud Function
        const uploadedPhotos = [];
        
        for (const photo of photos) {
          const photoFileName = `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.jpg`;
          
          // Get signed upload URL from Cloud Function
          const urlResult = await getUploadUrlFn({
            token: token,
            fileName: photoFileName,
            contentType: photo.file.type || 'image/jpeg'
          });
          
          // Upload to signed URL
          await fetch(urlResult.data.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': photo.file.type || 'image/jpeg' },
            body: photo.file
          });
          
          uploadedPhotos.push({
            url: urlResult.data.downloadUrl,
            storagePath: urlResult.data.storagePath,
            caption: photo.caption || photoCaption || null,
            section: photo.section || photoSection || null
          });
        }
        
        await submitContributionFn({
          inviteToken: token,
          contributorName: contributorName.trim(),
          relationship: relationship.trim() || null,
          type: 'photo',
          content: photoCaption || null,
          audioUrl: null,
          photos: uploadedPhotos
        });
      }
      
      setStep('success');
      
    } catch (err) {
      console.error('Error submitting contribution:', err);
      setSubmitError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================
  // Helpers
  // ============================================

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDisplayName = () => {
    if (!story) return 'your loved one';
    return story.preferredName || story.residentName || 'your loved one';
  };

  // ============================================
  // Loading / Error states
  // ============================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Oops!</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // ============================================
  // Welcome screen
  // ============================================

  if (step === 'welcome') {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
            <img 
              src="https://cdn.prod.website-files.com/6005bb130071e76d42c92ec9/608bf8a812544a0b1a4c7b3e_quiltt-logo-horiz.png" 
              alt="Quiltt" 
              className="h-8"
            />
            <span className="font-medium text-gray-400 text-sm">Life Story Capture</span>
          </div>
        </header>

        <main className="max-w-2xl mx-auto p-6">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-quiltt-purple/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-10 h-10 text-quiltt-purple" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Share a Memory of {getDisplayName()}
            </h1>
            <p className="text-gray-600 max-w-md mx-auto">
              {invite?.contributorName ? `Hi ${invite.contributorName}! ` : ''}
              You've been invited to contribute to {getDisplayName()}'s life story. 
              Choose how you'd like to share below.
            </p>
          </div>

          {/* Name input */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
            <input
              type="text"
              value={contributorName}
              onChange={(e) => setContributorName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent"
            />
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">
              Relationship to {getDisplayName()} (optional)
            </label>
            <input
              type="text"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              placeholder="e.g. Daughter, Neighbor, Friend"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent"
            />
          </div>

          {/* Contribution options */}
          <div className="space-y-3">
            <button
              onClick={() => setStep('memory')}
              className="w-full bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow text-left flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mic className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Share a Memory</h3>
                <p className="text-sm text-gray-500">Record a voice message or write a memory</p>
              </div>
            </button>

            <button
              onClick={() => setStep('photos')}
              className="w-full bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow text-left flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Camera className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Add Photos</h3>
                <p className="text-sm text-gray-500">Upload photos with captions</p>
              </div>
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ============================================
  // Memory recording step
  // ============================================

  if (step === 'memory') {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setStep('welcome'); resetRecording(); setMemoryText(''); }}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
              <span className="font-semibold text-gray-900">Share a Memory</span>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto p-6 space-y-4">
          {/* Record */}
          <div className="bg-white rounded-xl p-6 shadow-sm text-center">
            <h3 className="font-semibold text-gray-900 mb-4">Record a Voice Message</h3>
            
            {!audioBlob ? (
              <div>
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto transition-all ${
                    isRecording 
                      ? 'bg-red-500 hover:bg-red-600 recording-pulse' 
                      : 'bg-quiltt-purple hover:bg-purple-700'
                  }`}
                >
                  {isRecording ? (
                    <Square className="w-8 h-8 text-white fill-current" />
                  ) : (
                    <Mic className="w-8 h-8 text-white" />
                  )}
                </button>
                <p className="mt-3 text-gray-500 text-sm">
                  {isRecording ? formatDuration(duration) : 'Tap to record'}
                </p>
              </div>
            ) : (
              <div className="text-center">
                <button
                  onClick={playAudio}
                  className="w-16 h-16 bg-quiltt-purple/10 hover:bg-quiltt-purple/20 rounded-full flex items-center justify-center mx-auto transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6 text-quiltt-purple" />
                  ) : (
                    <Play className="w-6 h-6 text-quiltt-purple ml-1" />
                  )}
                </button>
                <p className="mt-2 text-gray-600">{formatDuration(duration)}</p>
                <button
                  onClick={resetRecording}
                  className="mt-3 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mx-auto"
                >
                  <RotateCcw className="w-4 h-4" />
                  Re-record
                </button>
              </div>
            )}
          </div>

          {/* Or write */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Or Write Your Memory</h3>
            <textarea
              value={memoryText}
              onChange={(e) => setMemoryText(e.target.value)}
              placeholder="Share a story, memory, or message..."
              rows={5}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent"
            />
          </div>

          {/* Section selector */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Tag className="w-5 h-5 text-quiltt-purple" />
              What part of their life is this about?
            </h3>
            <p className="text-sm text-gray-500 mb-4">Optional — helps us place it in the right section</p>
            <div className="grid grid-cols-2 gap-2">
              {PHOTO_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setMemorySection(section.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${
                    memorySection === section.id
                      ? 'border-quiltt-purple bg-purple-50 text-quiltt-purple'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span>{section.icon}</span>
                  <span className="text-sm truncate">{section.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Submit */}
          <button
            onClick={() => submitContribution('memory')}
            disabled={submitting || (!audioBlob && !memoryText.trim())}
            className="w-full py-4 bg-quiltt-purple hover:bg-purple-700 text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Submit Memory
              </>
            )}
          </button>

          {submitError && (
            <p className="text-center text-red-500 text-sm">{submitError}</p>
          )}
        </main>
      </div>
    );
  }

  // ============================================
  // Photos step
  // ============================================

  if (step === 'photos') {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setStep('welcome'); setPhotos([]); }}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
              <span className="font-semibold text-gray-900">Add Photos</span>
            </div>
            {photos.length > 0 && (
              <button
                onClick={() => submitContribution('photos')}
                disabled={submitting}
                className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Submit {photos.length} Photo{photos.length > 1 ? 's' : ''}
                  </>
                )}
              </button>
            )}
          </div>
        </header>

        <main className="max-w-2xl mx-auto p-6 space-y-4">
          {/* Photo grid */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Select Photos</h3>
            <div className="grid grid-cols-3 gap-3">
              {photos.map((photo, index) => (
                <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                  <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removePhoto(index)}
                    className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:bg-purple-50 transition-colors">
                  <Upload className="w-6 h-6 text-gray-400" />
                  <span className="text-xs text-gray-500 mt-1">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">Up to 5 photos</p>
          </div>

          {/* Section selector */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Tag className="w-5 h-5 text-quiltt-purple" />
              What part of their life are these from?
            </h3>
            <p className="text-sm text-gray-500 mb-4">Optional — helps us place photos in the right section</p>
            <div className="grid grid-cols-2 gap-2">
              {PHOTO_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setPhotoSection(section.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${
                    photoSection === section.id
                      ? 'border-quiltt-purple bg-purple-50 text-quiltt-purple'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span>{section.icon}</span>
                  <span className="text-sm truncate">{section.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Caption */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Caption (optional)</h3>
            <textarea
              value={photoCaption}
              onChange={(e) => setPhotoCaption(e.target.value)}
              placeholder="Add a caption or description for the photos..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent"
            />
          </div>

          {submitError && (
            <p className="text-center text-red-500 text-sm">{submitError}</p>
          )}
        </main>
      </div>
    );
  }

  // ============================================
  // Success screen
  // ============================================

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-8">
            Your contribution has been submitted and is pending review. 
            It will be added to {getDisplayName()}'s story once approved.
          </p>
          <button
            onClick={() => {
              setStep('welcome');
              resetRecording();
              setMemoryText('');
              setMemorySection('');
              setPhotos([]);
              setPhotoCaption('');
              setPhotoSection('');
            }}
            className="px-6 py-3 bg-quiltt-purple hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
          >
            Add Another Contribution
          </button>
        </div>
      </div>
    );
  }

  return null;
}
